import type { ApiRespBody, RequestMethod } from 'types/api';
import { trackEvent } from 'utils/ga';
import { ApiError, getApiErrorMessage } from "./errors";

/**
 * Parses the JSON returned by a network request
 */
function parseJSON<TData = unknown>(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json() as Promise<TData>;
}

/**
 * Normalizes an object for URLSearchParams
 * @param {Record<T, U>} obj some object to be normalize
 * @returns {URLSearchParams} a URLSearchParams object
 */
export function normalizeParams<T extends string, U extends string | number | boolean | undefined | null | (string | number)[]>(obj: Partial<Record<T, U>>) {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v: string | number) => params.append(key, v.toString()));
    } else {
      params.append(key, (value).toString());
    }
  });
  return params;
}

export function apiErrorDump(error: any) {
  try {
    const errorObject = JSON.parse(error.message);
    try {
      console.error(errorObject.originalError.info.message, '\n', errorObject);
    } catch {
      console.error(errorObject);
    }
  } catch {
    console.error(error);
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not.
 *
 * Bugbot L13 carve-out: 401 responses fall through to `parseJSON`
 * instead of throwing. The auth middleware (`server/utils/checkToken.js`)
 * now returns `401` *with* the legacy JSON envelope
 * (`{ success: false, message: 'Token is not valid' }`), so the dozens
 * of sagas that key off `result.message === 'Token is not valid'`
 * continue to work without a sweeping refactor. New callers that want
 * the cleaner contract can read the HTTP status directly via
 * `fetch`/`callCoreApi`.
 *
 * @throws {ApiError} An error with information about the request
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    trackKnownErrors(response);
    return response;
  }

  // 401: let the legacy JSON envelope reach saga handlers that already
  // dispatch logout/redirect actions on `message === 'Token is not valid'`.
  if (response.status === 401) {
    trackEvent('api_error', {
      error: 'unauthorized',
      error_url: response.url,
    });
    return response;
  }

  trackEvent('api_error', {
    error: 'non_200_response',
    error_url: response.url,
  });

  const error = new ApiError(response.statusText, response);
  throw error;
}

async function trackKnownErrors(response: Response) {
  const contentLength = response.headers.get('content-length');
  if (contentLength && parseInt(contentLength, 10) < 500) {
    const _clone = response.clone();
    const text = await _clone.text();
    if (text.includes('timeout')) {
      trackEvent('api_error', {
        error: 'timeout',
        error_url: _clone.url,
      });
      return;
    }
    if (text.includes('"success":false')) {
      trackEvent('api_error', {
        error: 'status_false',
        error_url: _clone.url,
      });
    }
  }
}

export function getDefaultRequestOptions<TPayload extends NonNullable<unknown>>(method: RequestMethod, authToken: string, payload?: TPayload, abortSignal?: AbortSignal): RequestInit {
  const body = (payload ? { body: JSON.stringify(payload) } : {});
  return {
    method,
    ...body,
    headers: {
      "Content-Type": "application/json",
      authorization: authToken,
    },
    ...(abortSignal ? { signal: abortSignal } : {})
  };
}

/**
 * Requests a URL, returning a promise.
 * Wrapper for fetch
 */
export default async function request<TData = unknown>(
  url: RequestInfo | URL,
  options?: RequestInit
) {
  // eslint-disable-next-line prettier/prettier
  return fetch(url, options).then(checkStatus).then(parseJSON as typeof parseJSON<TData>);
}

/** Returns `data` of a `{ success, data }` response, throwing the server's message on failure. */
export async function requestApiData<TData>(
  url: string,
  options: RequestInit,
  fallbackMessage: string
): Promise<TData | undefined> {
  let result: ApiRespBody<TData> | null;
  try {
    result = await request<ApiRespBody<TData>>(url, options);
  } catch (error) {
    throw new Error(await getApiErrorMessage(error, fallbackMessage));
  }
  if (result?.success) return result.data;
  throw new Error(result?.message || fallbackMessage);
}

/**
 * Call a Core API endpoint
 *
 * Routes to /core/{module}/{function} endpoints.
 *
 * @param endpoint - The endpoint path, e.g., 'workQueue/getWorkQueueComplete' or 'resources/getResources'
 * @param params - Parameters to pass as JSON body
 * @param authToken - Auth token from Redux store (use makeSelectAuthtoken selector)
 * @returns Promise with { success, data, error } shape
 *
 * @example
 * // Call workQueue endpoint
 * const result = await callCoreApi('workQueue/getWorkQueueComplete', { resourceIDs: ['R1'] });
 *
 * // Call resources endpoint
 * const result = await callCoreApi('resources/getResources', {});
 */
/**
 * Response shape from callCoreApi including optional optimistic update
 */
export interface CoreApiResponse<TData = any> {
  success: boolean;
  data?: TData;
  error?: string;
  /** Optimistic update payload from pipeline (DP-010) */
  optimisticUpdate?: {
    queryKey: string;
    data: any;
    params?: Record<string, any>;
    targetPath?: string;
    mergeStrategy?: 'replace' | 'merge' | 'append';
  };
}

export async function callCoreApi<TData = any>(
  endpoint: string,
  params?: Record<string, any>,
  authToken: string = ''
): Promise<CoreApiResponse<TData>> {
  try {
    // Build the URL - endpoint can be 'module/function' or just 'function' (defaults to workQueue)
    // Uses /api/core/* which is proxied to integration-common
    let url: string;
    if (endpoint.includes('/')) {
      url = `/api/core/${endpoint}`;
    } else {
      // Legacy support - single function name assumes workQueue module
      url = `/api/core/workQueue/${endpoint}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: authToken,
      },
      body: JSON.stringify(params || {}),
    });

    // Bugbot L13 carve-out: a 401 carries the legacy auth-failure
    // JSON envelope (`{ success: false, message: 'Token is not valid' }`).
    // Read it as JSON instead of treating it as an opaque HTTP error
    // so downstream code can branch on the message the same way the
    // legacy sagas already do.
    if (!response.ok && response.status !== 401) {
      const errorText = await response.text();
      return {
        success: false,
        error: `HTTP ${response.status}: ${errorText || response.statusText}`,
      };
    }

    const result = await response.json();

    // Handle API-level errors
    if (result.success === false) {
      return {
        success: false,
        error: result.error || result.message || 'API request failed',
      };
    }

    return {
      success: true,
      data: result.data,
      // Pass through optimisticUpdate from backend for pipeline-driven updates (DP-010)
      optimisticUpdate: result.optimisticUpdate,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Request failed',
    };
  }
}
